/**
 * AdminBadgeDesigner — visual editor for event badge templates.
 *
 * UX:
 *  • Left rail: list of templates (per tenant); New / Duplicate / Delete.
 *  • Centre: live preview (server-rendered PNG, refreshed on save/edit).
 *  • Right rail: properties panel (size, colours, logo URL, text blocks).
 *
 * Server endpoints used:
 *   GET    /api/badge-templates?tenant_id=...
 *   POST   /api/badge-templates
 *   PATCH  /api/badge-templates/{id}
 *   DELETE /api/badge-templates/{id}
 *   POST   /api/badge-templates/preview          (live inline preview)
 *   GET    /api/badge-templates/defaults
 *
 * Route: /admin/badge-designer
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Plus, Copy, Trash2, Save, RefreshCw, Image as ImageIcon, Layers, Type,
  Palette, Settings2, Printer,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';
import { MediaUpload } from '@/components/MediaUpload';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const PLACEHOLDERS = [
  '{first_name}', '{last_name}', '{full_name}', '{badge_code}',
  '{tier_name}', '{organisation}', '{event_name}',
];

const fetchPreview = async (template) => {
  const res = await axios.post(
    `${API}/api/badge-templates/preview`,
    { template },
    { headers: authH(), responseType: 'blob' }
  );
  return URL.createObjectURL(res.data);
};

// ---------------------------------------------------------------------------
// Right-rail editor
// ---------------------------------------------------------------------------
const ColourSwatch = ({ label, value, onChange, testId }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-500">{label}</Label>
    <div className="flex gap-2 items-center">
      <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)}
              className="h-8 w-12 bg-transparent border border-slate-700 rounded cursor-pointer"
              data-testid={testId} />
      <Input value={value || ''} onChange={e => onChange(e.target.value)}
             className="bg-slate-900 border-slate-800 text-xs" />
    </div>
  </div>
);

const TextBlockEditor = ({ block, onChange, onRemove, idx }) => (
  <Card className="bg-slate-900/40 border-slate-800">
    <CardContent className="p-3 space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-xs uppercase tracking-wider text-slate-500">
          Text block #{idx + 1}
        </span>
        <Button variant="ghost" size="sm" onClick={onRemove}
                 data-testid={`text-block-remove-${idx}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <Input value={block.content || ''}
             onChange={e => onChange({ ...block, content: e.target.value })}
             placeholder="Text or {placeholder}"
             className="bg-slate-900 border-slate-800 text-sm"
             data-testid={`text-block-content-${idx}`} />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div>
          <Label className="text-[10px] text-slate-500">X</Label>
          <Input type="number" value={block.x ?? 0}
                 onChange={e => onChange({ ...block, x: +e.target.value })}
                 className="bg-slate-900 border-slate-800 text-xs h-8" />
        </div>
        <div>
          <Label className="text-[10px] text-slate-500">Y</Label>
          <Input type="number" value={block.y ?? 0}
                 onChange={e => onChange({ ...block, y: +e.target.value })}
                 className="bg-slate-900 border-slate-800 text-xs h-8" />
        </div>
        <div>
          <Label className="text-[10px] text-slate-500">Size</Label>
          <Input type="number" value={block.font_size ?? 16}
                 onChange={e => onChange({ ...block, font_size: +e.target.value })}
                 className="bg-slate-900 border-slate-800 text-xs h-8" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 items-end">
        <ColourSwatch label="Text colour" value={block.color}
                       onChange={c => onChange({ ...block, color: c })} />
        <label className="flex items-center gap-2 text-xs text-slate-400 mb-1">
          <input type="checkbox" checked={!!block.bold}
                  onChange={e => onChange({ ...block, bold: e.target.checked })} />
          Bold
        </label>
      </div>
    </CardContent>
  </Card>
);

const AdminBadgeDesigner = () => {
  const qc = useQueryClient();
  const [tenantId, setTenantId] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null); // current edited template JSON
  const [name, setName] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  // Templates list
  const { data: templates = [] } = useQuery({
    queryKey: ['badge-templates', tenantId],
    queryFn: async () => {
      const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
      return (await axios.get(`${API}/api/badge-templates${qs}`, { headers: authH() })).data;
    },
  });

  // Defaults (initial new template payload)
  const { data: defaults } = useQuery({
    queryKey: ['badge-defaults'],
    queryFn: async () => (await axios.get(`${API}/api/badge-templates/defaults`,
       { headers: authH() })).data,
  });

  // Pick selected
  const selected = useMemo(
    () => templates.find(t => t.id === selectedId) || null,
    [templates, selectedId]
  );

  useEffect(() => {
    if (selected) {
      setDraft(selected.template);
      setName(selected.name || '');
    }
  }, [selected]);

  // Auto-refresh preview when draft changes (debounced)
  useEffect(() => {
    if (!draft) { setPreviewUrl(null); return; }
    const t = setTimeout(async () => {
      try {
        setBusy(true);
        const url = await fetchPreview(draft);
        setPreviewUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch (e) {
        // ignore intermediate errors
      } finally { setBusy(false); }
    }, 500);
    return () => clearTimeout(t);
  }, [draft]);

  // Mutations
  const create = useMutation({
    mutationFn: async () => {
      if (!tenantId) throw new Error('Pick a tenant first');
      const body = {
        tenant_id: tenantId,
        name: 'New Badge Template',
        template: defaults || {},
      };
      return (await axios.post(`${API}/api/badge-templates`, body, { headers: authH() })).data;
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['badge-templates'] });
      setSelectedId(d.id);
      toast.success('Template created');
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!selected) return;
      return (await axios.patch(`${API}/api/badge-templates/${selected.id}`,
        { name, template: draft }, { headers: authH() })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['badge-templates'] });
      toast.success('Saved');
    },
  });

  const duplicate = useMutation({
    mutationFn: async () => {
      return (await axios.post(`${API}/api/badge-templates/${selected.id}/duplicate`,
        null, { headers: authH() })).data;
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['badge-templates'] });
      setSelectedId(d.id);
      toast.success('Duplicated');
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      return (await axios.delete(`${API}/api/badge-templates/${selected.id}`,
        { headers: authH() })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['badge-templates'] });
      setSelectedId(null);
      setDraft(null);
      setPreviewUrl(null);
      toast.success('Deleted');
    },
  });

  // Bulk PDF — multi-page badge sheet for every delegate of a given event
  const [bulkEventId, setBulkEventId] = useState('');
  const [bulkPageSize, setBulkPageSize] = useState('auto');
  const [bulkBusy, setBulkBusy] = useState(false);
  const runBulkPdf = async () => {
    if (!selected) return;
    if (!bulkEventId.trim()) {
      toast.error('Enter an event ID');
      return;
    }
    setBulkBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/badge-templates/${selected.id}/bulk-pdf`,
        { event_id: bulkEventId.trim(), page_size: bulkPageSize },
        { headers: authH(), responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `badges-${selected.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      const count = res.headers['x-badge-count'];
      toast.success(`PDF generated${count ? ` — ${count} badges` : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PDF generation failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const updateDraft = (patch) => setDraft(prev => ({ ...(prev || {}), ...patch }));
  const updateBlock = (i, b) => {
    const arr = [...(draft.text_blocks || [])];
    arr[i] = b;
    updateDraft({ text_blocks: arr });
  };
  const addBlock = () => updateDraft({
    text_blocks: [...(draft.text_blocks || []),
      { x: 30, y: 250, font_size: 16, color: '#f8fafc', content: 'New text' }],
  });
  const removeBlock = (i) => {
    const arr = [...(draft.text_blocks || [])];
    arr.splice(i, 1);
    updateDraft({ text_blocks: arr });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-white inline-flex items-center gap-2">
              <Layers className="h-6 w-6 text-indigo-400" /> Badge Designer
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Design printable event badges. Universal QR is auto-stamped on every render.
            </p>
          </div>
          <Input value={tenantId} onChange={e => setTenantId(e.target.value)}
                  placeholder="Tenant ID (required for new templates)"
                  className="w-full max-w-[280px] bg-slate-900/60 border-slate-800"
                  data-testid="badge-tenant-input" />
        </header>

        <div className="grid grid-cols-12 gap-4">
          {/* Left rail */}
          <Card className="col-span-12 lg:col-span-3 bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-sm text-slate-200 flex items-center justify-between">
                Templates
                <Button size="sm" variant="outline" onClick={() => create.mutate()}
                         disabled={!tenantId || create.isPending}
                         data-testid="badge-new-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" />New
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 max-h-[600px] overflow-y-auto">
              {templates.length === 0 && (
                <p className="text-xs text-slate-500">No templates yet.</p>
              )}
              {templates.map(t => (
                <button
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition border ${
                    selectedId === t.id
                      ? 'bg-indigo-500/15 border-indigo-400/40 text-indigo-100'
                      : 'bg-slate-800/30 border-slate-800 hover:border-slate-700 text-slate-300'
                  }`}
                  data-testid={`badge-tpl-${t.id}`}
                >
                  <div className="truncate">{t.name}</div>
                  <div className="text-[10px] text-slate-500">{t.event_id || 'no event'}</div>
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Centre — live preview */}
          <Card className="col-span-12 lg:col-span-5 bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-sm text-slate-200 flex items-center justify-between">
                Preview
                {busy && <RefreshCw className="h-3.5 w-3.5 animate-spin text-slate-400" />}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex items-center justify-center min-h-[500px]">
              {previewUrl ? (
                <img src={previewUrl} alt="badge"
                      className="max-h-[640px] rounded-md shadow-2xl border border-slate-800"
                      data-testid="badge-preview-image" />
              ) : (
                <p className="text-slate-500 text-sm">
                  Select a template on the left, or click <strong>New</strong> to start.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Right rail — properties */}
          <Card className="col-span-12 lg:col-span-4 bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-sm text-slate-200 flex items-center justify-between">
                <span className="inline-flex items-center gap-2">
                  <Settings2 className="h-4 w-4" /> Properties
                </span>
                {selected && (
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={() => duplicate.mutate()}
                             data-testid="badge-duplicate-btn">
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => remove.mutate()}
                             data-testid="badge-delete-btn">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" onClick={() => save.mutate()}
                             disabled={save.isPending}
                             data-testid="badge-save-btn">
                      <Save className="h-3.5 w-3.5 mr-1" />Save
                    </Button>
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 max-h-[640px] overflow-y-auto">
              {!draft && (
                <p className="text-xs text-slate-500">Select a template to edit.</p>
              )}
              {draft && (
                <>
                  <div>
                    <Label className="text-xs text-slate-500">Template name</Label>
                    <Input value={name} onChange={e => setName(e.target.value)}
                           className="bg-slate-900 border-slate-800"
                           data-testid="badge-name-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-slate-500">Width</Label>
                      <Input type="number" value={draft.size?.width ?? 480}
                             onChange={e => updateDraft({ size: { ...(draft.size || {}), width: +e.target.value } })}
                             className="bg-slate-900 border-slate-800" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Height</Label>
                      <Input type="number" value={draft.size?.height ?? 760}
                             onChange={e => updateDraft({ size: { ...(draft.size || {}), height: +e.target.value } })}
                             className="bg-slate-900 border-slate-800" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <ColourSwatch label="Background"
                                    value={draft.background_color}
                                    onChange={c => updateDraft({ background_color: c })}
                                    testId="badge-bg-color" />
                    <ColourSwatch label="Accent / header"
                                    value={draft.accent_color}
                                    onChange={c => updateDraft({ accent_color: c })}
                                    testId="badge-accent-color" />
                  </div>
                  <div>
                    <MediaUpload value={draft.logo_url || ''} onChange={(url) => updateDraft({ logo_url: url })} purpose="logo" label="Logo" testId="badge-logo-upload" previewClassName="h-16" />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs text-slate-500">QR X</Label>
                      <Input type="number" value={draft.qr?.x ?? 0}
                             onChange={e => updateDraft({ qr: { ...(draft.qr || {}), x: +e.target.value } })}
                             className="bg-slate-900 border-slate-800" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">QR Y</Label>
                      <Input type="number" value={draft.qr?.y ?? 0}
                             onChange={e => updateDraft({ qr: { ...(draft.qr || {}), y: +e.target.value } })}
                             className="bg-slate-900 border-slate-800" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">QR Size</Label>
                      <Input type="number" value={draft.qr?.size ?? 260}
                             onChange={e => updateDraft({ qr: { ...(draft.qr || {}), size: +e.target.value } })}
                             className="bg-slate-900 border-slate-800" />
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-800">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs uppercase tracking-wider text-slate-500">
                        Text blocks
                      </span>
                      <Button size="sm" variant="outline" onClick={addBlock}
                               data-testid="badge-add-text-btn">
                        <Plus className="h-3.5 w-3.5 mr-1" />Add
                      </Button>
                    </div>
                    <p className="text-[10px] text-slate-500 mb-2">
                      Available placeholders: {PLACEHOLDERS.join(' · ')}
                    </p>
                    <div className="space-y-2">
                      {(draft.text_blocks || []).map((b, i) => (
                        <TextBlockEditor
                          key={i}
                          idx={i}
                          block={b}
                          onChange={nb => updateBlock(i, nb)}
                          onRemove={() => removeBlock(i)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Bulk PDF */}
                  <div className="pt-3 border-t border-slate-800 space-y-2">
                    <span className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">
                      <Printer className="h-3.5 w-3.5" /> Bulk print all badges
                    </span>
                    <Input value={bulkEventId} onChange={e => setBulkEventId(e.target.value)}
                           placeholder="Event ID (renders every delegate)"
                           className="bg-slate-900 border-slate-800 text-xs"
                           data-testid="bulk-pdf-event-id" />
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                      {['auto', 'letter', 'a4'].map(opt => (
                        <label key={opt}
                                className={`text-center py-1 rounded-md border cursor-pointer capitalize ${
                                  bulkPageSize === opt
                                    ? 'border-indigo-400/40 bg-indigo-500/15 text-indigo-200'
                                    : 'border-slate-800 bg-slate-800/30 text-slate-400 hover:border-slate-700'
                                }`}>
                          <input type="radio" name="page-size" value={opt} className="hidden"
                                  checked={bulkPageSize === opt}
                                  onChange={() => setBulkPageSize(opt)} />
                          {opt === 'auto' ? 'Badge size' : opt}
                        </label>
                      ))}
                    </div>
                    <Button className="w-full" onClick={runBulkPdf}
                             disabled={bulkBusy || !bulkEventId.trim()}
                             data-testid="bulk-pdf-btn">
                      {bulkBusy
                        ? <><RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />Generating…</>
                        : <><Printer className="h-3.5 w-3.5 mr-1" />Generate PDF</>}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminBadgeDesigner;
